import { useState, useEffect, useCallback, useRef } from "react";
import {
  Plus, Edit2, Eye, Copy, ToggleLeft, ToggleRight,
  Search, Package, ArrowLeft, Star, Layers,
  LayoutGrid, List, Download, Upload, Trash2, AlertTriangle,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { useToast } from "../hooks/use-toast";
import UniversalSinglePageForm from "../components/forms/UniversalSinglePageForm";
import DashboardLayout from "../components/DashboardLayout";

const API = process.env.REACT_APP_BACKEND_URL;

const LISTING_TYPES = [
  { value: "service",      label: "Service" },
  { value: "rental",       label: "Rental" },
  { value: "event",        label: "Event" },
  { value: "subscription", label: "Subscription" },
  { value: "product",      label: "Product" },
];

const STATUS_COLORS = {
  true:  "bg-green-100 text-green-800 border-green-200",
  false: "bg-gray-100 text-gray-500 border-gray-200",
};

async function apiFetch(path, opts = {}) {
  const token = sessionStorage.getItem("token");
  const res = await fetch(`${API}${path}`, {
    ...opts,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      ...(opts.headers || {}),
    },
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || "Request failed");
  }
  return res.json();
}

const VIEW = { LIST: "list", CREATE: "create", EDIT: "edit", PREVIEW: "preview" };

// ── Template Card (grid view) ─────────────────────────────────────────────────
function TemplateCard({ tpl, onEdit, onPreview, onDuplicate, onToggle, onDelete }) {
  return (
    <div
      className="bg-white rounded-xl border border-gray-200 p-4 hover:shadow-md transition-shadow flex flex-col gap-3"
      data-testid={`template-card-${tpl.templateId}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-gray-900 truncate text-sm">{tpl.templateName}</h3>
          {tpl.templateDescription && (
            <p className="text-xs text-gray-400 mt-0.5 line-clamp-2">{tpl.templateDescription}</p>
          )}
        </div>
        <span className={`shrink-0 px-2 py-0.5 rounded-full text-xs border font-medium ${STATUS_COLORS[String(tpl.isActive)]}`}>
          {tpl.isActive ? "Active" : "Inactive"}
        </span>
      </div>

      <div className="flex flex-wrap gap-1">
        <Badge variant="outline" className="capitalize text-xs">{tpl.listingType}</Badge>
        {tpl.category && (
          <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200">{tpl.category}</Badge>
        )}
        {tpl.subcategory && (
          <Badge variant="outline" className="text-xs bg-violet-50 text-violet-700 border-violet-200">{tpl.subcategory}</Badge>
        )}
      </div>

      <div className="flex items-center gap-4 text-xs text-gray-400">
        <span className="flex items-center gap-1">
          <Star className="h-3 w-3 text-amber-400" /> Priority {tpl.templatePriority ?? 1}
        </span>
        <span>{tpl.adoptionCount ?? 0} adoptions</span>
      </div>

      <div className="flex items-center gap-1 pt-2 border-t border-gray-100">
        <Button variant="ghost" size="sm" className="h-7 px-2" onClick={() => onPreview(tpl)} data-testid={`preview-card-${tpl.templateId}`}>
          <Eye className="h-3.5 w-3.5" />
        </Button>
        <Button variant="ghost" size="sm" className="h-7 px-2" onClick={() => onEdit(tpl)} data-testid={`edit-card-${tpl.templateId}`}>
          <Edit2 className="h-3.5 w-3.5" />
        </Button>
        <Button variant="ghost" size="sm" className="h-7 px-2" onClick={() => onDuplicate(tpl)} data-testid={`dup-card-${tpl.templateId}`}>
          <Copy className="h-3.5 w-3.5" />
        </Button>
        <Button variant="ghost" size="sm" className="h-7 px-2" onClick={() => onToggle(tpl)} data-testid={`toggle-card-${tpl.templateId}`}>
          {tpl.isActive
            ? <ToggleRight className="h-4 w-4 text-green-500" />
            : <ToggleLeft className="h-4 w-4 text-gray-400" />}
        </Button>
        <Button variant="ghost" size="sm" className="h-7 px-2 text-red-500 hover:text-red-700 hover:bg-red-50 ml-auto" onClick={() => onDelete(tpl)} data-testid={`delete-card-${tpl.templateId}`}>
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function AdminMasterCatalog() {
  const { toast } = useToast();
  const importRef = useRef(null);

  // list state
  const [templates, setTemplates]   = useState([]);
  const [total, setTotal]           = useState(0);
  const [loading, setLoading]       = useState(true);
  const [searchQ, setSearchQ]       = useState("");
  const [filterType, setFilterType] = useState("");
  const [filterCat, setFilterCat]   = useState("");
  const [filterSub, setFilterSub]   = useState("");
  const [page, setPage]             = useState(1);
  const [viewMode, setViewMode]     = useState("list"); // "list" | "grid"
  const [importLoading, setImportLoading] = useState(false);

  // delete confirmation
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, tpl: null });

  // dropdown data
  const [categories, setCategories]       = useState([]);
  const [subcategories, setSubcategories] = useState([]);

  // view state
  const [view, setView]         = useState(VIEW.LIST);
  const [activeTemplate, setActive] = useState(null);

  // ── Fetch category/subcategory lists ───────────────────────────────────────
  const fetchCategories = useCallback(async () => {
    try {
      const data = await apiFetch("/api/master-catalog/templates/admin/categories");
      setCategories(data.categories || []);
    } catch (_) {}
  }, []);

  const fetchSubcategories = useCallback(async (cat) => {
    if (!cat || cat === "all") { setSubcategories([]); return; }
    try {
      const data = await apiFetch(`/api/master-catalog/templates/admin/subcategories?category=${encodeURIComponent(cat)}`);
      setSubcategories(data.subcategories || []);
    } catch (_) {}
  }, []);

  useEffect(() => { fetchCategories(); }, [fetchCategories]);
  useEffect(() => { fetchSubcategories(filterCat); }, [filterCat, fetchSubcategories]);

  // ── Data fetch ─────────────────────────────────────────────────────────────
  const fetchTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 20, includeAll: true });
      if (filterType) params.set("listingType", filterType);
      if (filterCat)  params.set("category", filterCat);
      if (filterSub)  params.set("subcategory", filterSub);
      const data = await apiFetch(`/api/master-catalog/templates/admin/list?${params}`);
      setTemplates(data.templates || []);
      setTotal(data.total || 0);
    } catch (e) {
      toast({ title: "Error", description: e.message, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  }, [page, filterType, filterCat, filterSub, toast]);

  useEffect(() => { fetchTemplates(); }, [fetchTemplates]);

  // ── Actions ────────────────────────────────────────────────────────────────
  const handleToggleActive = async (tpl) => {
    try {
      const res = await apiFetch(`/api/master-catalog/templates/${tpl.templateId}/toggle-active`, { method: "POST" });
      toast({ title: res.isActive ? "Template Activated" : "Template Deactivated" });
      fetchTemplates();
    } catch (e) {
      toast({ title: "Error", description: e.message, variant: "destructive" });
    }
  };

  const handleDuplicate = async (tpl) => {
    try {
      await apiFetch(`/api/master-catalog/templates/${tpl.templateId}/duplicate`, { method: "POST" });
      toast({ title: "Duplicated", description: `"${tpl.templateName} (Copy)" created as inactive.` });
      fetchTemplates();
    } catch (e) {
      toast({ title: "Error", description: e.message, variant: "destructive" });
    }
  };

  const handleDelete = async (tpl) => {
    setDeleteConfirm({ open: true, tpl });
  };

  const confirmDelete = async () => {
    const tpl = deleteConfirm.tpl;
    setDeleteConfirm({ open: false, tpl: null });
    try {
      await apiFetch(`/api/master-catalog/templates/${tpl.templateId}`, { method: "DELETE" });
      toast({ title: "Template Deleted", description: `"${tpl.templateName}" has been permanently deleted.` });
      fetchTemplates();
    } catch (e) {
      toast({ title: "Error", description: e.message, variant: "destructive" });
    }
  };

  const handleMigrateLegacy = async () => {
    try {
      const res = await apiFetch("/api/master-catalog/templates/migrate-legacy", { method: "POST" });
      toast({ title: "Migration complete", description: `${res.modified} legacy templates marked inactive.` });
    } catch (e) {
      toast({ title: "Error", description: e.message, variant: "destructive" });
    }
  };

  // ── Export ─────────────────────────────────────────────────────────────────
  const handleExport = async () => {
    try {
      const params = new URLSearchParams();
      if (filterType) params.set("listingType", filterType);
      if (filterCat)  params.set("category", filterCat);
      const data = await apiFetch(`/api/master-catalog/templates/admin/export?${params}`);
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement("a");
      a.href     = url;
      a.download = `master-templates-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast({ title: "Export complete", description: `${data.count} templates exported.` });
    } catch (e) {
      toast({ title: "Export failed", description: e.message, variant: "destructive" });
    }
  };

  // ── Import ─────────────────────────────────────────────────────────────────
  const handleImportFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImportLoading(true);
    try {
      const token    = sessionStorage.getItem("token");
      const formData = new FormData();
      formData.append("file", file);
      const res = await fetch(`${API}/api/master-catalog/templates/admin/bulk-import`, {
        method:  "POST",
        headers: { Authorization: `Bearer ${token}` },
        body:    formData,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Import failed");
      }
      const result = await res.json();
      toast({
        title:       "Import complete",
        description: `Created: ${result.created}, Updated: ${result.updated}${result.errors?.length ? `, Errors: ${result.errors.length}` : ""}`,
      });
      fetchTemplates();
      fetchCategories();
    } catch (e) {
      toast({ title: "Import failed", description: e.message, variant: "destructive" });
    } finally {
      setImportLoading(false);
      if (importRef.current) importRef.current.value = "";
    }
  };

  const handleTemplateSaved = () => {
    setView(VIEW.LIST);
    setActive(null);
    fetchTemplates();
    fetchCategories();
  };

  // ── Filtered display ───────────────────────────────────────────────────────
  const displayed = searchQ
    ? templates.filter(t =>
        t.templateName.toLowerCase().includes(searchQ.toLowerCase()) ||
        (t.category || "").toLowerCase().includes(searchQ.toLowerCase()) ||
        (t.subcategory || "").toLowerCase().includes(searchQ.toLowerCase())
      )
    : templates;

  const grouped = displayed.reduce((acc, t) => {
    const key = t.category || "Uncategorised";
    if (!acc[key]) acc[key] = [];
    acc[key].push(t);
    return acc;
  }, {});

  // ── Render: LIST view ──────────────────────────────────────────────────────
  if (view === VIEW.LIST) return (
    <DashboardLayout>
      <div className="px-6 py-6">

        {/* Delete Confirmation Modal */}
        {deleteConfirm.open && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
              <div className="flex items-start gap-4">
                <div className="p-2 bg-red-100 rounded-full shrink-0">
                  <AlertTriangle className="h-5 w-5 text-red-600" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-900 text-lg">Delete Template</h3>
                  <p className="text-sm text-gray-500 mt-1">
                    Are you sure you want to permanently delete <span className="font-medium text-gray-800">"{deleteConfirm.tpl?.templateName}"</span>?
                    This action cannot be undone.
                  </p>
                </div>
              </div>
              <div className="flex justify-end gap-3 mt-6">
                <Button variant="outline" onClick={() => setDeleteConfirm({ open: false, tpl: null })} data-testid="cancel-delete-btn">
                  Cancel
                </Button>
                <Button className="bg-red-600 hover:bg-red-700 text-white" onClick={confirmDelete} data-testid="confirm-delete-btn">
                  <Trash2 className="h-4 w-4 mr-1.5" /> Delete
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Layers className="h-6 w-6" /> Master Catalogue Templates
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              Universal dynamic templates vendors use to create listings. {total} templates total.
            </p>
          </div>
          <div className="flex gap-2 flex-wrap justify-end">
            <Button variant="outline" size="sm" onClick={handleMigrateLegacy} data-testid="migrate-legacy-btn">
              Mark Legacy
            </Button>
            <Button variant="outline" size="sm" onClick={handleExport} data-testid="export-btn">
              <Download className="h-4 w-4 mr-1" /> Export
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => importRef.current?.click()}
              disabled={importLoading}
              data-testid="import-btn"
            >
              <Upload className="h-4 w-4 mr-1" />
              {importLoading ? "Importing..." : "Import"}
            </Button>
            <input
              ref={importRef}
              type="file"
              accept=".json"
              className="hidden"
              onChange={handleImportFile}
              data-testid="import-file-input"
            />
            <Button onClick={() => setView(VIEW.CREATE)} data-testid="create-template-btn">
              <Plus className="h-4 w-4 mr-1" /> New Template
            </Button>
          </div>
        </div>

        {/* Filters + View Toggle */}
        <div className="bg-white rounded-xl border border-gray-200 p-4 mb-6 flex flex-wrap gap-3 items-center">
          <div className="relative flex-1 min-w-48">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Search templates..."
              value={searchQ}
              onChange={e => setSearchQ(e.target.value)}
              className="pl-9"
              data-testid="template-search"
            />
          </div>

          <Select value={filterType || "all"} onValueChange={v => { setFilterType(v === "all" ? "" : v); setPage(1); }}>
            <SelectTrigger className="w-36" data-testid="filter-type">
              <SelectValue placeholder="Listing Type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              {LISTING_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
            </SelectContent>
          </Select>

          <Select
            value={filterCat || "all"}
            onValueChange={v => { setFilterCat(v === "all" ? "" : v); setFilterSub(""); setPage(1); }}
          >
            <SelectTrigger className="w-40" data-testid="filter-category">
              <SelectValue placeholder="Category" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {categories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>

          <Select
            value={filterSub || "all"}
            onValueChange={v => { setFilterSub(v === "all" ? "" : v); setPage(1); }}
            disabled={!filterCat}
          >
            <SelectTrigger className="w-40" data-testid="filter-subcategory">
              <SelectValue placeholder="Subcategory" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Subcategories</SelectItem>
              {subcategories.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>

          {/* View Toggle */}
          <div className="flex rounded-lg border border-gray-200 overflow-hidden ml-auto" data-testid="view-toggle">
            <button
              className={`px-3 py-1.5 flex items-center gap-1.5 text-sm transition-colors ${
                viewMode === "list" ? "bg-gray-900 text-white" : "bg-white text-gray-500 hover:bg-gray-50"
              }`}
              onClick={() => setViewMode("list")}
              data-testid="view-list-btn"
            >
              <List className="h-4 w-4" /> List
            </button>
            <button
              className={`px-3 py-1.5 flex items-center gap-1.5 text-sm transition-colors ${
                viewMode === "grid" ? "bg-gray-900 text-white" : "bg-white text-gray-500 hover:bg-gray-50"
              }`}
              onClick={() => setViewMode("grid")}
              data-testid="view-grid-btn"
            >
              <LayoutGrid className="h-4 w-4" /> Grid
            </button>
          </div>
        </div>

        {/* Content */}
        {loading ? (
          <div className="flex justify-center py-16">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900" />
          </div>
        ) : displayed.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-200 p-16 text-center">
            <Package className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 font-medium">No templates yet</p>
            <p className="text-sm text-gray-400 mt-1">Create your first Universal Dynamic Template</p>
            <Button className="mt-4" onClick={() => setView(VIEW.CREATE)}>
              <Plus className="h-4 w-4 mr-1" /> New Template
            </Button>
          </div>
        ) : viewMode === "grid" ? (
          /* Grid View */
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="template-grid">
            {displayed.map(tpl => (
              <TemplateCard
                key={tpl.templateId}
                tpl={tpl}
                onEdit={t => { setActive(t); setView(VIEW.EDIT); }}
                onPreview={t => { setActive(t); setView(VIEW.PREVIEW); }}
                onDuplicate={handleDuplicate}
                onToggle={handleToggleActive}
                onDelete={handleDelete}
              />
            ))}
          </div>
        ) : (
          /* List View — grouped by category */
          Object.entries(grouped).map(([cat, items]) => (
            <div key={cat} className="mb-6" data-testid={`category-group-${cat}`}>
              <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-2 px-1">{cat}</h3>
              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-100 bg-gray-50">
                      <th className="text-left px-4 py-3 font-medium text-gray-600">Template</th>
                      <th className="text-left px-4 py-3 font-medium text-gray-600">Subcategory</th>
                      <th className="text-left px-4 py-3 font-medium text-gray-600">Type</th>
                      <th className="text-left px-4 py-3 font-medium text-gray-600 hidden md:table-cell">Priority</th>
                      <th className="text-left px-4 py-3 font-medium text-gray-600 hidden md:table-cell">Adopted</th>
                      <th className="text-left px-4 py-3 font-medium text-gray-600">Status</th>
                      <th className="text-right px-4 py-3 font-medium text-gray-600">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map(tpl => (
                      <tr key={tpl.templateId} className="border-b border-gray-50 hover:bg-gray-50 transition-colors">
                        <td className="px-4 py-3">
                          <div className="font-medium text-gray-900">{tpl.templateName}</div>
                          {tpl.templateDescription && (
                            <div className="text-xs text-gray-400 truncate max-w-48">{tpl.templateDescription}</div>
                          )}
                        </td>
                        <td className="px-4 py-3 text-gray-600">{tpl.subcategory || "—"}</td>
                        <td className="px-4 py-3">
                          <Badge variant="outline" className="capitalize text-xs">{tpl.listingType}</Badge>
                        </td>
                        <td className="px-4 py-3 hidden md:table-cell">
                          <span className="flex items-center gap-1 text-amber-500">
                            <Star className="h-3 w-3" /> {tpl.templatePriority ?? 1}
                          </span>
                        </td>
                        <td className="px-4 py-3 hidden md:table-cell text-gray-500">{tpl.adoptionCount ?? 0}</td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-0.5 rounded-full text-xs border font-medium ${STATUS_COLORS[String(tpl.isActive)]}`}>
                            {tpl.isActive ? "Active" : "Inactive"}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              variant="ghost" size="sm"
                              onClick={() => { setActive(tpl); setView(VIEW.PREVIEW); }}
                              data-testid={`preview-${tpl.templateId}`}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost" size="sm"
                              onClick={() => { setActive(tpl); setView(VIEW.EDIT); }}
                              data-testid={`edit-${tpl.templateId}`}
                            >
                              <Edit2 className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost" size="sm"
                              onClick={() => handleDuplicate(tpl)}
                              data-testid={`duplicate-${tpl.templateId}`}
                            >
                              <Copy className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost" size="sm"
                              onClick={() => handleToggleActive(tpl)}
                              data-testid={`toggle-${tpl.templateId}`}
                            >
                              {tpl.isActive
                                ? <ToggleRight className="h-4 w-4 text-green-500" />
                                : <ToggleLeft className="h-4 w-4 text-gray-400" />}
                            </Button>
                            <Button
                              variant="ghost" size="sm"
                              className="text-red-500 hover:text-red-700 hover:bg-red-50"
                              onClick={() => handleDelete(tpl)}
                              data-testid={`delete-${tpl.templateId}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))
        )}

        {/* Pagination */}
        {total > 20 && (
          <div className="flex justify-center gap-2 mt-4">
            <Button variant="outline" size="sm" disabled={page === 1} onClick={() => setPage(p => p - 1)}>Previous</Button>
            <span className="text-sm text-gray-500 self-center">Page {page}</span>
            <Button variant="outline" size="sm" disabled={templates.length < 20} onClick={() => setPage(p => p + 1)}>Next</Button>
          </div>
        )}
      </div>
    </DashboardLayout>
  );

  // ── Render: CREATE view ────────────────────────────────────────────────────
  if (view === VIEW.CREATE) return (
    <DashboardLayout>
      <UniversalSinglePageForm
        formPurpose="template"
        noLayout={true}
        categoryOverride="service"
        templateMeta={{ templateName: "", templateDescription: "", templatePriority: 1 }}
        onTemplateSave={handleTemplateSaved}
        onTemplateCancel={() => setView(VIEW.LIST)}
      />
    </DashboardLayout>
  );

  // ── Render: EDIT view ──────────────────────────────────────────────────────
  if (view === VIEW.EDIT && activeTemplate) return (
    <DashboardLayout>
      <UniversalSinglePageForm
        formPurpose="template"
        noLayout={true}
        categoryOverride={activeTemplate.listingType}
        templateId={activeTemplate.templateId}
        initialData={{ ...activeTemplate.defaultFieldValues }}
        templateMeta={{
          templateName:        activeTemplate.templateName,
          templateDescription: activeTemplate.templateDescription,
          templateImage:       activeTemplate.templateImage,
          templatePriority:    activeTemplate.templatePriority ?? 1,
        }}
        onTemplateSave={handleTemplateSaved}
        onTemplateCancel={() => { setView(VIEW.LIST); setActive(null); }}
      />
    </DashboardLayout>
  );

  // ── Render: PREVIEW view ───────────────────────────────────────────────────
  if (view === VIEW.PREVIEW && activeTemplate) return (
    <DashboardLayout>
      <div className="px-6 py-4">
        <div className="flex items-center gap-3 mb-4">
          <Button variant="ghost" size="sm" onClick={() => { setView(VIEW.LIST); setActive(null); }} data-testid="back-to-list-preview">
            <ArrowLeft className="h-4 w-4 mr-1" /> Templates
          </Button>
          <h2 className="text-xl font-bold text-gray-900">Preview: {activeTemplate.templateName}</h2>
          <Badge className="bg-amber-100 text-amber-800 border-amber-200">Read-only vendor view</Badge>
        </div>
        <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-sm text-amber-800 mb-4">
          This is exactly what vendors will see when they choose this template. All fields are pre-filled with template defaults.
        </div>
        <UniversalSinglePageForm
          formPurpose="template-preview"
          noLayout={true}
          categoryOverride={activeTemplate.listingType}
          initialData={{
            ...activeTemplate.defaultFieldValues,
            title:       activeTemplate.templateName,
            description: activeTemplate.templateDescription,
          }}
          onTemplateCancel={() => { setView(VIEW.LIST); setActive(null); }}
        />
      </div>
    </DashboardLayout>
  );

  return null;
}
